import React, { useState, useRef, useEffect, isValidElement, cloneElement, useCallback } from 'react';
import { createUseStyles, clsx } from '@pushpay/styles';
import { useConstant, useIsMounted, useDebounce, useOnClickOutside } from '@pushpay/utils';
import { mapTriggerEvents } from './helper';
import { TooltipPanel } from './tooltip-panel';
export const useStyles = createUseStyles({
  root: {
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  tooltip: {},
  content: {},
  tooltipArrow: {}
});
const DEFAULT_DELAY = 150;

const callback = cb => cb(); // replace with useId()


let tooltipCounter = 0;

const Tooltip = props => {
  const {
    content,
    placement,
    children,
    onDisplay = () => {},
    onHide = () => {},
    classes: classesProp,
    className: classNameProp,
    panelSpace,
    animationDuration,
    delay = DEFAULT_DELAY,
    fixedContainer,
    defaultVisible = false,
    'data-pp-at-target': targetId,
    disabled = false,
    onClick,
    ...rest
  } = props;
  const classes = useStyles(classesProp);
  const className = clsx(classNameProp, classes.root);
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);
  const isMounted = useIsMounted();
  const [delayCallback] = useDebounce(callback, delay);
  const display = useCallback(() => delayCallback(() => {
    if (isMounted() && !visible) {
      onDisplay();
      setVisible(true);
    }
  }), [delayCallback, isMounted, onDisplay, visible]);
  const hide = useCallback(() => delayCallback(() => {
    if (isMounted() && visible) {
      onHide();
      setVisible(false);
    }
  }), [delayCallback, isMounted, onHide, visible]);

  const toggle = () => visible ? hide() : display();

  useEffect(function setVisibleByDefault() {
    if (defaultVisible) {
      display();
    }
  }, [defaultVisible, display]);
  useEffect(function hideWhenDisabling() {
    if (disabled) {
      setVisible(false);
    }
  }, [disabled]);
  useOnClickOutside([containerRef], () => setVisible(false), {
    listening: visible,
    useEscapeKey: true
  });
  const tooltipId = useConstant(() => `tooltip-${tooltipCounter++}`);
  const childElement = /*#__PURE__*/isValidElement(children) ? children : /*#__PURE__*/React.createElement("span", null, children);

  const createTrigger = () => {
    var _childElement$props$t;

    return /*#__PURE__*/cloneElement(childElement, {
      tabIndex: (_childElement$props$t = childElement.props.tabIndex) !== null && _childElement$props$t !== void 0 ? _childElement$props$t : 0,
      'aria-label': !visible && typeof content === 'string' ? content : undefined,
      'aria-labelledby': visible ? tooltipId : undefined,
      ...(!disabled && mapTriggerEvents({
        trigger: ['focus', 'hover', 'touch'],
        display,
        hide,
        toggle,
        childProps: childElement.props
      }))
    });
  };

  return /*#__PURE__*/React.createElement("div", Object.assign({
    ref: containerRef,
    "data-pp-at-target": `${targetId}-container`,
    className: className
  }, rest), disabled ? childElement : createTrigger(), /*#__PURE__*/React.createElement(TooltipPanel, {
    id: tooltipId,
    fixedContainer: fixedContainer,
    visible: visible,
    containerRef: containerRef,
    placement: placement,
    className: classes.tooltip,
    classes: classes,
    "data-pp-at-target": `${targetId}-panel`,
    space: panelSpace,
    animationDuration: disabled ? 0 : animationDuration
  }, typeof content === 'string' ? content : content()));
};

export default Tooltip;